import { useRef } from 'react';
import { useFramework, useIdentity, useMoveEventListener, useScale, useStageScrollPosition } from '@/framework';
import { REFERENCE_LINE_DIRECTION, RULER_BAR_SIZE, type ReferenceLine } from '@/version-types';
import { AddReferenceLine } from '../operations/add-reference-line';
import { DeleteReferenceLine } from '../operations/delete-reference-line';
import { UpdateReferenceLine } from '../operations/update-reference-line';
import { useReferenceLinesState } from '../states/reference-lines-state';
import { getContainerRect } from '../utils';

// 从标尺处拖动添加参考线
export function useAddReferenceLineFromRuler(paddingLeft: number, paddingTop: number) {
    const identity = useIdentity();
    const framework = useFramework();
    const scale = useScale();

    const minPosition = useRef(Infinity);
    const isDragging = useRef(false);
    const draggingReferenceLine = useRef<ReferenceLine | null>(null);

    const addReferenceLine = new AddReferenceLine(identity);
    const deleteReferenceLine = new DeleteReferenceLine(identity);
    const updateReferenceLine = new UpdateReferenceLine(identity);

    const referenceLinesState = useReferenceLinesState();
    const setDraggingLineId = referenceLinesState((state) => state.setDraggingLineId);
    const setTooltipPosition = referenceLinesState((state) => state.setTooltipPosition);
    const setDraggingLine = referenceLinesState((state) => state.setDraggingLine);
    const setContainerRect = referenceLinesState((state) => state.setContainerRect);
    const setRulerBarSize = referenceLinesState((state) => state.setRulerBarSize);
    const stageScrollPosition = useStageScrollPosition();
    // 拖动事件
    useMoveEventListener((e) => {
        // 点击的是标尺
        const isOnRuler = e.target.classList.contains('l-ruler-bar__canvas');
        if (!isOnRuler) return;

        const isHorizontal = e.target.classList.contains('horizontal-ruler');

        // 拖拽开始
        if (e.isStart) {
            const rect = e.target.getBoundingClientRect();
            minPosition.current = isHorizontal
                ? rect.top + rect.height - stageScrollPosition.top
                : rect.left + rect.width - stageScrollPosition.left;
            isDragging.current = true;
            framework.operate.disableMouseSelect();

            const containerRect = getContainerRect();
            if (containerRect) {
                setContainerRect(containerRect);
                const hasRulerBar = !!document.querySelector('.l-ruler-bar__canvas');
                const rulerBarSize = hasRulerBar ? RULER_BAR_SIZE : 0;
                setRulerBarSize(rulerBarSize);
            }
        }
        // 拖拽结束
        else if (e.isEnd) {
            if (draggingReferenceLine.current) {
                const { direction, position, id } = draggingReferenceLine.current;
                // 为了避免产生历史记录, 需要删除添加过程中的临时参考线后, 再重新添加一条
                deleteReferenceLine.delete(id, false);

                // 只有拖动距离超过标尺, 才添加参考线
                const lastPosition = isHorizontal ? e.currentPoint.y : e.currentPoint.x;
                if (lastPosition > minPosition.current) {
                    // 添加一条带历史记录的参考线
                    addReferenceLine.addWithLineData({
                        direction,
                        position,
                    });
                }
            }

            setDraggingLineId('');
            setDraggingLine(null);
            draggingReferenceLine.current = null;
            minPosition.current = Infinity;
            isDragging.current = false;
            framework.operate.enableMouseSelect();
        }
        // 拖拽中
        else {
            if (!isDragging.current) return;

            const position = isHorizontal
                ? (e.currentPoint.y - minPosition.current - paddingTop + RULER_BAR_SIZE) / scale
                : (e.currentPoint.x - minPosition.current - paddingLeft + RULER_BAR_SIZE) / scale;

            // 设置 tooltip 的位置
            setTooltipPosition({
                x: e.currentPoint.x,
                y: e.currentPoint.y,
            });

            // 已经添加参考线, 则更新参考线位置
            if (draggingReferenceLine.current) {
                const newLine = {
                    ...draggingReferenceLine.current,
                    position,
                };
                updateReferenceLine.update(newLine, undefined, false);
                setDraggingLine(newLine);
                draggingReferenceLine.current.position = position;
            }
            // 还没添加参考线, 线判断看看是否达到添加参考线的条件
            else {
                const isOverMinPosition = isHorizontal
                    ? e.currentPoint.y >= minPosition.current
                    : e.currentPoint.x >= minPosition.current;

                // 达到距离(拖动超过标尺)就添加参考线
                if (isOverMinPosition) {
                    const direction = isHorizontal
                        ? REFERENCE_LINE_DIRECTION.HORIZONTAL
                        : REFERENCE_LINE_DIRECTION.VERTICAL;

                    const result = addReferenceLine.addWithLineData(
                        {
                            direction,
                            position,
                        },
                        false,
                    );

                    setDraggingLineId(result.id);
                    setDraggingLine(result);
                    draggingReferenceLine.current = result;
                }
            }
        }
    });
}
